import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { useBranches } from '@/hooks/api/branches';
import { useCategories } from '@/hooks/use-categories';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, Search } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { useToast } from '@/hooks/use-toast';
import { usePagination } from '@/hooks/use-pagination';
import { PaginationControls } from '@/components/PaginationControls';
import { StockDialog, type MovementType } from '@/components/inventory/StockDialog';
import { useTranslation } from 'react-i18next';

type Stock = components['schemas']['StockResource'];
type StockMovement = components['schemas']['StockMovementResource'];
type Product = components['schemas']['ProductResource'];

const dateLocale = (lng: string) => lng === 'ru' ? 'ru-RU' : lng === 'en' ? 'en-US' : 'uz';

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message) || fallback;
  }
  return fallback;
}

export default function Inventory() {
  const { t, i18n } = useTranslation();
  const { data: stocksData, isLoading: loadingStocks } = useQuery({
    queryKey: ['/stocks'],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/stocks');
      if (error) throw new Error(errorMessage(error, t('cat_common.loadStocksError')));
      return data;
    },
  });
  const stocks: Stock[] = stocksData?.data ?? [];

  const { data: movementsData, isLoading: loadingMovements } = useQuery({
    queryKey: ['/stock-movements'],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/stock-movements');
      if (error) throw new Error(errorMessage(error, t('cat_inventory.loadMovementsError')));
      return data;
    },
  });
  const movements: StockMovement[] = movementsData?.data ?? [];

  // FR-98: deactivated products too, so old stock rows and movements keep their names (as Lovable's join);
  // the movement dialogs still offer only active ones.
  const { data: productsData } = useQuery({
    queryKey: ['/products', { params: { query: { include_inactive: 1 } } }],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/products', { params: { query: { include_inactive: 1 } as never } });
      if (error) throw new Error(errorMessage(error, t('cat_common.loadProductsError')));
      return data;
    },
  });
  const products: Product[] = productsData?.data ?? [];
  const activeProducts = products.filter(p => p.is_active);

  const { data: categories = [] } = useCategories();

  const { data: branches = [] } = useBranches();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [showAdd, setShowAdd] = useState(false);
  const [openStockId, setOpenStockId] = useState<number | null>(null);
  const [searchStock, setSearchStock] = useState('');
  const [searchMovement, setSearchMovement] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [movementTypeFilter, setMovementTypeFilter] = useState('all');

  const categoryNames: string[] = categories.map((c) => c.name!);
  const productById = new Map(products.map(p => [p.id, p]));

  const filteredStocks = stocks.filter(s => {
    const prod = productById.get(s.product_id!);
    const q = searchStock.toLowerCase();
    const matchSearch = !q
      || (prod?.name?.toLowerCase().includes(q) ?? false)
      || (prod?.sku?.toLowerCase().includes(q) ?? false);
    const matchCat = selectedCategory === 'all' || prod?.category === selectedCategory;
    return matchSearch && matchCat;
  });

  const filteredMovements = movements.filter(sm => {
    const prod = productById.get(sm.product_id!);
    const matchSearch = prod?.name?.toLowerCase().includes(searchMovement.toLowerCase()) ?? false;
    const matchType = movementTypeFilter === 'all' || sm.movement_type === movementTypeFilter;
    return matchSearch && matchType;
  });

  const stockPagination = usePagination(filteredStocks, 25);
  const movementPagination = usePagination(filteredMovements, 25);

  const movementMutation = useMutation({
    mutationFn: async (params: { product_id: number; movement_type: string; quantity: number; reference?: string }) => {
      const branchId = branches[0]?.id;
      if (!branchId) throw new Error(t('cat_inventory.branchNotFound'));
      const { error } = await apiClient.POST('/stock-movements', {
        body: { branch_id: branchId, ...params },
      });
      if (error) throw new Error(errorMessage(error, t('cat_common.error')));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/stocks'] });
      queryClient.invalidateQueries({ queryKey: ['/stock-movements'] });
    },
  });

  const openStock = stocks.find(s => s.id === openStockId) || null;
  const openStockProduct = openStock ? productById.get(openStock.product_id!) ?? null : null;
  const openStockMovements = openStock
    ? movements.filter(m => m.product_id === openStock.product_id)
    : [];

  const submitMovement = async (params: { product_id: string; movement_type: MovementType; quantity: number; reference?: string }) => {
    try {
      await movementMutation.mutateAsync({ ...params, product_id: Number(params.product_id) });
      toast({ title: t('cat_inventory.movementSaved') });
      setShowAdd(false);
      setOpenStockId(null);
    } catch (err: any) {
      toast({ title: t('cat_common.error'), description: err.message, variant: 'destructive' });
    }
  };

  const zeroOutStock = async () => {
    if (!openStock) return;
    try {
      await movementMutation.mutateAsync({
        product_id: openStock.product_id!,
        movement_type: 'ADJUST',
        quantity: 0,
        reference: t('cat_inventory.zeroedReference'),
      });
      toast({ title: t('cat_inventory.zeroedToast') });
      setOpenStockId(null);
    } catch (err: any) {
      toast({ title: t('cat_common.error'), description: err.message, variant: 'destructive' });
    }
  };

  const typeLabel: Record<string, string> = {
    IN: t('cat_common.in'), OUT: t('cat_common.out'), TRANSFER: t('cat_common.transfer'), ADJUST: t('cat_common.adjust'),
  };

  const typeColor: Record<string, string> = {
    IN: 'bg-success/10 text-success', OUT: 'bg-destructive/10 text-destructive',
    TRANSFER: 'bg-info/10 text-info', ADJUST: 'bg-warning/10 text-warning',
  };

  const stockExportData = filteredStocks.map((s) => {
    const prod = productById.get(s.product_id!);
    return {
      sku: prod?.sku || '', mahsulot: prod?.name || '', brend: prod?.brend_nomi || '',
      kategoriya: prod?.category || '', miqdor: s.quantity, birlik: prod?.unit || '',
      yangilangan: s.updated_at ? new Date(s.updated_at).toLocaleDateString(dateLocale(i18n.language)) : '',
    };
  });
  const stockExportColumns = [
    { header: 'SKU', key: 'sku' }, { header: t('cat_common.product'), key: 'mahsulot' },
    { header: t('cat_common.brand'), key: 'brend' }, { header: t('cat_common.category'), key: 'kategoriya' },
    { header: t('cat_common.quantity'), key: 'miqdor' }, { header: t('cat_common.unit'), key: 'birlik' },
    { header: t('cat_inventory.updated'), key: 'yangilangan' },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-foreground">{t('cat_inventory.title')}</h1>
        <div className="flex items-center gap-2">
          <ExportButtons data={stockExportData} columns={stockExportColumns} fileName="ombor" title={t('cat_inventory.stockTitle')} />
          <Button size="sm" onClick={() => setShowAdd(true)}>
            <Plus size={14} className="mr-1" /> {t('cat_inventory.inOut')}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="stock">
        <TabsList>
          <TabsTrigger value="stock">{t('cat_inventory.tabStock')}</TabsTrigger>
          <TabsTrigger value="movements">{t('cat_inventory.tabMovements')}</TabsTrigger>
        </TabsList>

        <TabsContent value="stock">
          <div className="flex items-center gap-3 mb-4 mt-4">
            <div className="relative flex-1 max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder={t('cat_common.search')} value={searchStock} onChange={e => setSearchStock(e.target.value)} className="pl-9" />
            </div>
            <div className="flex gap-1 overflow-x-auto">
              <button onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${selectedCategory === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}>
                {t('cat_common.all')}
              </button>
              {categoryNames.map(cat => (
                <button key={cat} onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${selectedCategory === cat ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}>
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">SKU</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.product')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.brand')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.category')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.quantity')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.unit')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.status')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_inventory.updated')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingStocks ? (
                    <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">{t('cat_common.loading')}</td></tr>
                  ) : filteredStocks.length === 0 ? (
                    <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">{t('cat_inventory.empty')}</td></tr>
                  ) : (
                    (stockPagination.items as Stock[]).map(s => {
                      const prod = productById.get(s.product_id!);
                      const qty = Number(s.quantity);
                      return (
                        <tr
                          key={s.id}
                          className="border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer"
                          onClick={() => setOpenStockId(s.id!)}
                        >
                          <td className="p-3 text-sm text-muted-foreground font-mono">{prod?.sku || '—'}</td>
                          <td className="p-3 text-sm text-foreground font-medium">{prod?.name}</td>
                          <td className="p-3 text-sm text-muted-foreground">{prod?.brend_nomi || '—'}</td>
                          <td className="p-3 text-sm text-muted-foreground">{prod?.category}</td>
                          <td className="p-3 text-sm font-medium text-foreground">{s.quantity}</td>
                          <td className="p-3 text-sm text-muted-foreground">{prod?.unit}</td>
                          <td className="p-3">
                            {qty === 0 ? (
                              <Badge className="text-xs border-0 bg-destructive/10 text-destructive">{t('cat_common.stockOut')}</Badge>
                            ) : qty < 10 ? (
                              <Badge className="text-xs border-0 bg-warning/10 text-warning">{t('cat_common.stockLow')}</Badge>
                            ) : (
                              <Badge className="text-xs border-0 bg-success/10 text-success">{t('cat_common.stockOk')}</Badge>
                            )}
                          </td>
                          <td className="p-3 text-xs text-muted-foreground">{s.updated_at ? new Date(s.updated_at).toLocaleDateString(dateLocale(i18n.language)) : '—'}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <PaginationControls page={stockPagination.page} totalPages={stockPagination.totalPages} total={stockPagination.total} pageSize={stockPagination.pageSize} onPrev={stockPagination.prevPage} onNext={stockPagination.nextPage} hasPrev={stockPagination.hasPrev} hasNext={stockPagination.hasNext} />
          </div>
        </TabsContent>

        <TabsContent value="movements">
          <div className="flex items-center gap-3 mb-4 mt-4">
            <div className="relative flex-1 max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder={t('cat_common.search')} value={searchMovement} onChange={e => setSearchMovement(e.target.value)} className="pl-9" />
            </div>
            <div className="flex gap-1">
              {[
                { key: 'all', label: t('cat_common.all') },
                { key: 'IN', label: t('cat_common.in') },
                { key: 'OUT', label: t('cat_common.out') },
                { key: 'ADJUST', label: t('cat_common.adjust') },
                { key: 'TRANSFER', label: t('cat_common.transfer') },
              ].map(f => (
                <button key={f.key} onClick={() => setMovementTypeFilter(f.key)}
                  className={`px-3 py-1.5 rounded-full text-xs transition-colors ${movementTypeFilter === f.key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.product')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_inventory.type')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.quantity')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_inventory.note')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_inventory.date')}</th>
                </tr>
              </thead>
              <tbody>
                {loadingMovements ? (
                  <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">{t('cat_common.loading')}</td></tr>
                ) : filteredMovements.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">{t('cat_inventory.movementsNotFound')}</td></tr>
                ) : (
                  (movementPagination.items as StockMovement[]).map(sm => {
                    const prod = productById.get(sm.product_id!);
                    return (
                      <tr key={sm.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                        <td className="p-3 text-sm text-foreground">{prod?.name}</td>
                        <td className="p-3"><Badge className={`text-xs border-0 ${typeColor[sm.movement_type!] || ''}`}>{typeLabel[sm.movement_type!] || sm.movement_type}</Badge></td>
                        <td className="p-3 text-sm text-foreground">{sm.quantity}</td>
                        <td className="p-3 text-sm text-muted-foreground">{sm.reference}</td>
                        <td className="p-3 text-xs text-muted-foreground">{sm.created_at ? new Date(sm.created_at).toLocaleDateString(dateLocale(i18n.language)) : '—'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            <PaginationControls page={movementPagination.page} totalPages={movementPagination.totalPages} total={movementPagination.total} pageSize={movementPagination.pageSize} onPrev={movementPagination.prevPage} onNext={movementPagination.nextPage} hasPrev={movementPagination.hasPrev} hasNext={movementPagination.hasNext} />
          </div>
        </TabsContent>
      </Tabs>

      {/* Yangi ombor harakati — to'liq ekran */}
      <StockDialog
        open={showAdd}
        onOpenChange={setShowAdd}
        products={activeProducts as any[]}
        saving={movementMutation.isPending}
        onSubmit={submitMovement}
      />

      {/* Mavjud qoldiq — to'liq ekran */}
      <StockDialog
        open={!!openStockId}
        onOpenChange={v => { if (!v) setOpenStockId(null); }}
        products={activeProducts as any[]}
        product={openStockProduct}
        currentQty={openStock ? Number(openStock.quantity) : null}
        movements={openStockMovements}
        saving={movementMutation.isPending}
        onSubmit={submitMovement}
        onZeroOut={zeroOutStock}
      />
    </div>
  );
}
